import { format } from "date-fns";
import { toZonedTime } from "date-fns-tz";
import type { AlarmSchedule } from "@visioni/a3s-alarm-schedules";
import { AlarmException } from "@src/services/api/types";
import { View } from "@src/components";
import { useUnistyles } from "react-native-unistyles";
import { memo, useMemo, FC } from "react";
import {
  buildDaySegments,
  getSegmentLabels,
  type DaySegment,
  type SegmentLabel,
} from "@src/features/schedules/utils/schedule-utils";
import { CurrentTimeIndicator } from "./CurrentTimeIndicator";
import { DayColumnHeader } from "./DayColumnHeader";
import { ScheduleBarSvg } from "./ScheduleBarSvg";
import { TimeLabels } from "./TimeLabels";

interface DayColumnProps {
  date: Date;
  exceptionColors: Record<string, string>;
  isToday: boolean;
  schedule?: AlarmSchedule;
  exceptions?: AlarmException[];
  timezone: string;
}

const BAR_HEIGHT = 216; // same visual height as before (24 slots * 9px each)

export const DayColumn: FC<DayColumnProps> = memo(
  ({ date, exceptionColors, isToday, schedule, exceptions, timezone }) => {
    const { theme } = useUnistyles();

    const todayZoned = toZonedTime(new Date(), timezone);
    const todayDateOnly = new Date(todayZoned.getFullYear(), todayZoned.getMonth(), todayZoned.getDate());
    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const isPastDay = dateOnly < todayDateOnly;

    // date is already zoned via toZonedTime in WeeklySchedule — use format (local getters), not formatInTimeZone
    const shortDay = format(date, "EEEEEE");
    const dayName = shortDay === "Mo" || shortDay === "We" || shortDay === "Fr" ? shortDay[0] : shortDay;
    const dateString = format(date, "dd");

    // Compute segments and labels via useMemo
    const segments = useMemo<DaySegment[]>(() => {
      if (!schedule) return [];
      return buildDaySegments(schedule, exceptions ?? [], date);
    }, [schedule, exceptions, date]);

    const labels = useMemo<SegmentLabel[]>(() => getSegmentLabels(segments), [segments]);

    const trackBg = theme.colors.alarmStates.trackBackground as string;
    const pastTrackBg = theme.colors.alarmStates.pastTrackBackground as string;

    return (
      <View flex={1} py="4xl" alignItems="center" bg="surface">
        {/* Header (day name + date badge) */}
        <DayColumnHeader dayName={dayName} dateString={dateString} isToday={isToday} isPastDay={isPastDay} />

        {/* Track container */}
        <View
          width="97%"
          style={{
            position: "relative",
            overflow: "visible",
            backgroundColor: isPastDay ? pastTrackBg : trackBg,
          }}
        >
          <ScheduleBarSvg
            segments={segments}
            isPastDay={isPastDay}
            barHeight={BAR_HEIGHT}
            exceptions={exceptions}
            exceptionColors={exceptionColors}
            schedule={schedule}
          />

          {/* Today indicator — horizontal line with dot at current time */}
          {isToday && <CurrentTimeIndicator timezone={timezone} barHeight={BAR_HEIGHT} />}

          {/* Time labels — positioned inside the disarmed segment */}
          <TimeLabels labels={labels} barHeight={BAR_HEIGHT} />
        </View>
      </View>
    );
  },
  (prevProps, nextProps) => {
    if (
      prevProps.date.getTime() !== nextProps.date.getTime() ||
      prevProps.isToday !== nextProps.isToday ||
      prevProps.schedule !== nextProps.schedule ||
      prevProps.exceptionColors !== nextProps.exceptionColors ||
      prevProps.timezone !== nextProps.timezone
    ) {
      return false;
    }

    if (
      prevProps.exceptions?.length !== nextProps.exceptions?.length ||
      prevProps.exceptions?.some((ex, idx) => ex.id !== nextProps.exceptions?.[idx]?.id)
    ) {
      return false;
    }

    return true;
  },
);
